import { useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCreateQuote, useInvalidateQuote, useLot } from '../api/hooks';
import type { Quote } from '../api/types';
import { OfflineBanner, TopBar } from '../components/chrome';
import { DurationEditor } from '../components/duration-editor';
import { QuoteStatus } from '../components/quote-status';
import { Cta, Muted, Screen } from '../components/ui';
import { useApiError } from '../i18n/useApiError';
import { durationLabel, usd } from '../lib/format';
import { lotExpiryLabel, resolveLotTimeLanguage } from '../lib/lot-time';
import { useCountdown } from '../lib/use-countdown';

export function DurationPage() {
  const { t } = useTranslation();
  const describeError = useApiError();
  const { lotId } = useParams({ from: '/lot/$lotId/duration' });
  const { vehicleId, spaceNumber } = useSearch({ from: '/lot/$lotId/duration' }) as {
    vehicleId: string;
    spaceNumber: string;
  };
  const navigate = useNavigate();
  const lot = useLot(lotId);
  const { mutateAsync: createQuote, isPending } = useCreateQuote();
  const { mutateAsync: invalidateQuote } = useInvalidateQuote();
  const [requestedMinutes, setRequestedMinutes] = useState(60);
  const [quote, setQuote] = useState<Quote | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [retry, setRetry] = useState(0);
  const sequence = useRef(0);
  const activeQuoteId = useRef<string | null>(null);
  const spaceNumberForQuote = lot.data
    ? lot.data.spaceNumberingEnabled !== false
      ? Number(spaceNumber)
      : null
    : undefined;

  const changeDuration = (minutes: number) => {
    if (minutes === requestedMinutes) return;
    sequence.current += 1;
    const previous = activeQuoteId.current;
    activeQuoteId.current = null;
    setQuote(null);
    setError(null);
    if (previous) void invalidateQuote(previous).catch(() => undefined);
    setRequestedMinutes(minutes);
  };

  // Duration ↔ departure is a semantic change even at the same minute count,
  // and an expired hold is equally dead: both paths must discard the old quote
  // and request a fresh one (review P0-2).
  const requote = () => {
    sequence.current += 1;
    const previous = activeQuoteId.current;
    activeQuoteId.current = null;
    setQuote(null);
    setError(null);
    if (previous) void invalidateQuote(previous).catch(() => undefined);
    setRetry((value) => value + 1);
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: retry is an explicit request revision.
  useEffect(() => {
    const current = ++sequence.current;
    if (requestedMinutes < 1 || spaceNumberForQuote === undefined) return;
    const timer = window.setTimeout(() => {
      void createQuote({
        lotId,
        vehicleId,
        spaceNumber: spaceNumberForQuote,
        durationMinutes: requestedMinutes,
      })
        .then((result) => {
          if (sequence.current !== current) {
            void invalidateQuote(result.quote.id).catch(() => undefined);
            return;
          }
          activeQuoteId.current = result.quote.id;
          setQuote(result.quote);
        })
        .catch((cause) => {
          if (sequence.current === current) {
            setError(describeError(cause, 'confirm.quoteFailed'));
          }
        });
    }, 350);
    return () => window.clearTimeout(timer);
  }, [
    createQuote,
    describeError,
    invalidateQuote,
    lotId,
    requestedMinutes,
    retry,
    spaceNumberForQuote,
    vehicleId,
  ]);

  return (
    <>
      <TopBar title={t('duration.title')} />
      <OfflineBanner />
      {/* Deliberately NOT wide (review §4.13 asks for wide home/detail/confirm
          only): the duration column stays 520px on desktop so the inputs and
          the summary read as one narrow form. pb-0 is for the phone's flush
          sticky footer; at lg the footer is static (see QuoteFooter), so the
          screen takes its normal bottom padding back. */}
      <Screen className="pb-0 lg:pb-12">
        {/* The top bar already says "How long?" — the body only carries what
            the bar lacks: which lot this price is for (review §5). */}
        <Muted>{t('duration.subtitle', { lot: lot.data?.name ?? '' })}</Muted>
        <DurationEditor
          value={requestedMinutes}
          onChange={changeDuration}
          onModeChange={requote}
          timezone={lot.data?.timezone}
        />

        {quote && quote.breakdown.appliedRate.source !== 'tier' ? (
          <div className="rounded-lg bg-surface-muted px-3 py-2.5 text-xs font-medium text-warning">
            {t('duration.rateApplied', { rate: quote.breakdown.appliedRate.name })}
          </div>
        ) : null}

        {error ? (
          <button
            type="button"
            onClick={() => {
              setError(null);
              setRetry((value) => value + 1);
            }}
            className="rounded-lg border border-danger p-3 text-left text-[13px] text-danger"
          >
            {error} · <span className="font-semibold text-accent">{t('common.retry')}</span>
          </button>
        ) : null}

        <QuoteFooter
          quote={quote}
          requestedMinutes={requestedMinutes}
          timezone={lot.data?.timezone}
          loading={isPending && !quote}
          onRequote={requote}
          onReview={() =>
            quote &&
            void navigate({
              to: '/lot/$lotId/confirm',
              params: { lotId },
              search: { quoteId: quote.id },
            })
          }
        />
      </Screen>
    </>
  );
}

function QuoteFooter({
  quote,
  requestedMinutes,
  timezone,
  loading,
  onRequote,
  onReview,
}: {
  quote: Quote | null;
  requestedMinutes: number;
  timezone: string | undefined;
  loading: boolean;
  onRequote: () => void;
  onReview: () => void;
}) {
  const { t, i18n } = useTranslation();
  const remaining = useCountdown(
    quote?.expiresAt ?? '1970-01-01T00:00:00.000Z',
    quote
      ? Math.max(
          0,
          Math.floor((new Date(quote.expiresAt).getTime() - new Date(quote.createdAt).getTime()) / 1000),
        )
      : 0,
  );
  const expectedExpiry = quote
    ? new Date(new Date(quote.startAt).getTime() + quote.entitlementMinutes * 60_000)
    : null;

  return (
    // Sticky on phones, where the thumb expects the total pinned above the
    // keyboard. On a tall desktop viewport the same sticky bar would strand
    // the summary at the bottom of an empty column, far from the inputs it
    // describes (review §4.13 “让摘要靠近输入内容”), so at lg it becomes a
    // static block that sits directly after the editor.
    <div className="sticky bottom-0 -mx-[18px] flex flex-col gap-3 border-t border-hairline bg-white px-[18px] pb-4 pt-3 lg:static">
      <div className="flex items-center gap-3">
        <div className="flex flex-1 flex-col gap-0.5">
          <Muted>{t('duration.total')}</Muted>
          {/* Quote state lives in QuoteStatus alone; this slot only ever
              describes the duration being priced. */}
          <span className={`text-sm font-semibold ${quote ? '' : 'text-muted'}`}>
            {quote
              ? quote.requestedMinutes === quote.entitlementMinutes
                ? t('duration.validFor', { duration: durationLabel(quote.entitlementMinutes, t) })
                : t('duration.roundedValue', {
                    requested: durationLabel(quote.requestedMinutes, t),
                    entitlement: durationLabel(quote.entitlementMinutes, t),
                  })
              : requestedMinutes > 0
                ? durationLabel(requestedMinutes, t)
                : t('duration.enterValidTime')}
          </span>
        </div>
        <span className="num text-3xl font-bold">{quote ? usd(quote.totalCents) : '—'}</span>
      </div>
      {quote && expectedExpiry ? (
        <div className="num text-[11px] text-muted">
          {t('duration.estimatedEnd', {
            time: lotExpiryLabel(expectedExpiry, timezone, resolveLotTimeLanguage(i18n.language), t),
          })}
        </div>
      ) : null}
      {/* Below one minute nothing is being quoted, so no state would be true. */}
      {quote || requestedMinutes >= 1 ? (
        <QuoteStatus quoteId={quote?.id ?? null} remaining={remaining} onRequote={onRequote} />
      ) : null}
      <Cta
        label={t('duration.review')}
        note={
          quote ? t('duration.reviewNote', { amount: usd(quote.totalCents) }) : t('duration.waitingQuote')
        }
        loading={loading}
        disabled={quote?.status !== 'valid' || remaining <= 0}
        onClick={onReview}
      />
    </div>
  );
}
